import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import Dotdotdot from 'react-dotdotdot';
import {FormattedDate, FormattedMessage} from 'react-intl';

import CBTRedirection from '@stubhub/react-cbt-redirection';
import {getEventTime, getEventDate, getVenueInfo} from '@stubhub/react-event-helper';
import FeatureToggle from '@stubhub/react-feature-toggle';
import FormattedLink from '@stubhub/react-formatted-link';
import {getSellSearchSuggestEventUrl} from '@stubhub/sell-url-helper';

import {APP} from '../../constants';
import {emphasizeSearchTerm} from '../../utils';
import {SearchListItemMessages} from '../SearchSuggestionList/messages';

import './SearchSuggestionListItem.scss';

const SearchSuggestionListItem = ({
  term,
  searchSuggestion,
  selected,
  onListItemClick,
  isRedirectModalEnabled,
  modalRef,
  app,
}) => {
  const linkRef = useRef(null);

  const handleClick = (e) => (linkRef.current ? linkRef.current.click() : onListItemClick({searchSuggestion})(e));

  const {type, notClickable, webURI} = searchSuggestion || {};
  let listItemClassName = `SearchSuggestionListItem__item ${type}`;

  if (selected) {
    listItemClassName += ' selected';
  }

  let searchSuggestionListItem;
  let searchSuggestionDivider;

  switch (type) {
    case 'header': {
      const {text, icon} = searchSuggestion;
      let imageSource;
      if (icon) {
        imageSource = require(`../../../styles/images/${icon}`).default;
      }
      if (text !== 'VENUES') {
        searchSuggestionListItem = (
          <div className="SearchSuggestionListItem__itemInfo header">
            {icon && <img src={imageSource} alt="" />}
            <span className="SearchSuggestionListItem__header">
              <FormattedMessage {...SearchListItemMessages[text]} />
            </span>
          </div>
        );
      } else if (text === 'VENUES') {
        searchSuggestionListItem = (
          <FeatureToggle name="common.enableVenueSearchSuggestion">
            <div className="SearchSuggestionListItem__itemInfo header">
              {icon && <img src={imageSource} alt="" />}
              <span className="SearchSuggestionListItem__header">
                <FormattedMessage {...SearchListItemMessages[text]} />
              </span>
            </div>
          </FeatureToggle>
        );
      }
      break;
    }
    case 'event_item': {
      const {displayTitle} = searchSuggestion;
      const eventDate = getEventDate(searchSuggestion);
      const formattedEventDate = eventDate ? (
        <div className="SearchSuggestionListItem__eventDate">
          <FormattedDate value={eventDate} weekday="short" />, <FormattedDate value={eventDate} month="short" />{' '}
          <FormattedDate value={eventDate} day="numeric" />, <FormattedDate value={eventDate} year="numeric" />
        </div>
      ) : null;
      const formattedEventTime = (
        <div className="SearchSuggestionListItem__eventTime">{getEventTime(searchSuggestion)}</div>
      );
      const {venueName, venueLocation, venueCountry} = getVenueInfo(searchSuggestion);
      searchSuggestionListItem = (
        <div className="SearchSuggestionListItem__itemInfo event">
          <div className="SearchSuggestionListItem__eventDescription">
            <div className="SearchSuggestionListItem__eventName">
              <CBTRedirection
                isEnabled={app !== APP.SELL && isRedirectModalEnabled}
                countryCode={venueCountry}
                location="search"
                uri={app === APP.SELL ? getSellSearchSuggestEventUrl(webURI) : webURI}
                mode="optional"
                onClick={() => onListItemClick({searchSuggestion})}
                linkRef={linkRef}
                modalRef={modalRef}
              >
                <span
                  className="SearchSuggestionListItem__link--item"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: emphasizeSearchTerm(displayTitle, term),
                  }}
                />
              </CBTRedirection>
            </div>
            <div className="SearchSuggestionListItem__venueName">
              <Dotdotdot clamp={2}>
                {venueName}, {venueLocation}
              </Dotdotdot>
            </div>
          </div>
          <div className="SearchSuggestionListItem__eventInfo">
            {formattedEventDate}
            {formattedEventTime}
          </div>
        </div>
      );
      break;
    }
    case 'link_item': {
      searchSuggestionListItem = (
        <div className="SearchSuggestionListItem__itemInfo">
          <FormattedLink className="SearchSuggestionListItem__link--item seeAll">
            <FormattedMessage id="Search.Results.MoreEvents" defaultMessage="More events" />
          </FormattedLink>
        </div>
      );
      break;
    }
    case 'performer_item': {
      searchSuggestionListItem = (
        <div className="SearchSuggestionListItem__itemInfo SearchSuggestionListItem__itemInfo--Performer">
          <FormattedLink
            className="SearchSuggestionListItem__link--item"
            dangerouslySetInnerHTML={{
              __html: emphasizeSearchTerm(searchSuggestion.name, term),
            }}
          />
        </div>
      );
      break;
    }
    case 'venue_item': {
      searchSuggestionListItem = (
        <FeatureToggle name="common.enableVenueSearchSuggestion">
          <div className="SearchSuggestionListItem__itemInfo SearchSuggestionListItem__itemInfo--Venue">
            <FormattedLink
              className="SearchSuggestionListItem__link--item"
              dangerouslySetInnerHTML={{
                __html: emphasizeSearchTerm(searchSuggestion.name, term),
              }}
            />
            <span className="upcoming">
              {' '}
              {searchSuggestion.eventCount} <FormattedMessage {...SearchListItemMessages.UPCOMING} />
            </span>
          </div>
        </FeatureToggle>
      );
      break;
    }
    case 'footer': {
      searchSuggestionListItem = (
        <div className="SearchSuggestionListItem__itemInfo">
          <FormattedLink className="SearchSuggestionListItem__link--item seeAll">
            <span>
              <FormattedMessage
                id="Search.Result.SeeAllAbout"
                defaultMessage="See all results from {term}"
                values={{
                  term: <span className="SearchSuggestionListItem__term--em">{term}</span>,
                }}
              />
            </span>
          </FormattedLink>
        </div>
      );
      searchSuggestionDivider = <div className="SearchSuggestionListItem__divider" />;
      break;
    }

    default:
      break;
  }

  const onClickHandler = (e) => {
    if (notClickable) {
      return;
    }
    handleClick(e);
  };

  return (
    <div className={listItemClassName} onClick={onClickHandler}>
      {searchSuggestionDivider}
      {searchSuggestionListItem}
    </div>
  );
};

SearchSuggestionListItem.propTypes = {
  /**
   * Handler function invoked on list item click.
   */
  onListItemClick: PropTypes.func,

  /**
   * Object representing the search suggestion.
   */
  searchSuggestion: PropTypes.object,

  /**
   * Search term for highlighting purposes.
   */
  term: PropTypes.string,

  /**
   * Flag indicating if the item is selected.
   */
  selected: PropTypes.bool,

  /**
   * Ref to the cbt modal.
   */
  modalRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})]),

  /**
   * Application name.
   */
  app: PropTypes.string,

  /**
   * Flag indicating if modal redirection is enabled.
   */
  isRedirectModalEnabled: PropTypes.bool,
};

export default SearchSuggestionListItem;
